import { SnapshotBodyCache } from "./snapshot-cache";
/**
 * API Client for Python FastAPI backend
 * Handles all communication with the backend server
 */

import { auth } from "@/lib/firebase";

import { API_BASE_URL } from "./config";

const snapshotBodies = new SnapshotBodyCache<AlertSnapshot>();

export class ApiClientError extends Error {
  status: number;
  reason?: string;
  data: any;
  bodyText: string;

  constructor(
    message: string,
    {
      status,
      reason,
      data,
      bodyText,
    }: { status: number; reason?: string; data: any; bodyText: string },
  ) {
    super(message);
    this.name = "ApiClientError";
    this.status = status;
    this.reason = reason;
    this.data = data;
    this.bodyText = bodyText;
  }
}

export function isPaidRequiredError(error: unknown): boolean {
  const err = error as Partial<ApiClientError> & { message?: string };
  return (
    err?.reason === "paid_required" ||
    err?.status === 402 ||
    Boolean(err?.message?.includes("paid_required"))
  );
}

async function buildApiError(response: Response, prefix: string): Promise<ApiClientError> {
  const bodyText = await response.text();
  let data: any = null;
  try {
    data = bodyText ? JSON.parse(bodyText) : null;
  } catch {
    data = null;
  }
  const detail = data?.detail;
  const reason =
    (detail && typeof detail === "object" ? detail.reason : undefined) ??
    data?.reason;
  const bodyMessage =
    (detail && typeof detail === "object" ? detail.message : undefined) ??
    (typeof detail === "string" ? detail : undefined) ??
    data?.message ??
    bodyText;
  return new ApiClientError(`${prefix}: ${response.status} ${bodyMessage}`, {
    status: response.status,
    reason,
    data,
    bodyText,
  });
}

export interface SessionUpdateRequest {
  title?: string;
  status?: string;
  messages?: Array<Record<string, any>>;
  requested_documentation?: string[];
  visibility?: "private" | "public";
}

export interface SessionResponse {
  user_id: string;
  session_id: string;
  notebook_id: string;
  title?: string;
  status: string;
  created_at: string;
  updated_at: string;
  message_count: number;
  visibility?: "private" | "public";
}

// Updated message interface to handle new format
export interface MessageContent {
  type: "text" | "image_url";
  text?: string;
  image_url?: {
    url: string;
  };
}

export interface ToolCall {
  id: string;
  type: "function";
  function: {
    name: string;
    arguments: string; // JSON string of arguments
  };
}

export interface BackendMessage {
  role: string;
  content: string | MessageContent[]; // Can be string or array
  reasoning?: string;
  tool_call_id?: string; // For tool messages
  tool_name?: string; // Name of the tool that was called
  name?: string; // Alternative field for tool name
  tool_calls?: ToolCall[]; // For assistant messages with tool calls
  id?: string; // Message ID
  created_at?: string; // UTC timestamp from backend
  sub_agents?: boolean; // Whether this message was sent with sub-agents enabled
  skill_id?: string; // Skill launch (no-blob): skill that launched this message
  skill_ids?: string[]; // Multiple stacked skills that launched this message
}

export interface SessionWithMessages extends SessionResponse {
  messages?: BackendMessage[];
  read_only?: boolean; // Backend determines if user can edit this session
}

export interface Vote {
  chatId: string;
  messageId: string;
  isUpvoted: boolean;
}

export interface BackendVote {
  session_id: string;
  messageId: string;
  isUpvoted: boolean;
}

export interface VoteRequest {
  session_id: string;
  messageId: string;
  type: "up" | "down";
}

export interface VoteResponse {
  status: string;
  message: string;
  session_id: string;
  messageId: string;
  type: string;
}

export interface MobileHintStatusResponse {
  should_show: boolean;
}

/**
 * Alert doc shape returned by GET /alerts/{id} and GET /alerts.
 * Matches xynth-backend/app/api/routes/alerts.py::_serialize_alert output.
 */
export interface AlertDoc {
  _id: string;
  /**
   * Discriminator set by the backend on the raw alert doc. `"signal"` for
   * track-record signals (PnL dashboard + green accent), otherwise a plain
   * email alert. Absent on legacy docs — treat as a non-signal alert.
   */
  kind?: "signal" | "alert" | string;
  name: string;
  description?: string;
  schedule: string;
  schedule_description?: string;
  notification_email: string;
  status: "active" | "paused" | "testing" | "test_failed" | "errored" | string;
  notebook_id?: string;
  parent_notebook_id?: string;
  draft_notebook_id?: string;
  swap_in_progress?: boolean;
  session_id?: string;
  linked_session_id?: string;
  /**
   * Owning Firebase UID — authoritative for ownership checks. Present for
   * owners; the backend STRIPS this field from non-owner responses (see
   * `_serialize_alert` in alerts.py) so a stranger can't learn which user
   * actually built a curated "influencer" alert. Treat as optional from
   * the frontend's perspective.
   */
  user_id?: string;
  /**
   * Optional creator-slug pointer. When set (e.g. "jason_luongo"), the
   * public alert page renders under that creator's byline instead of the
   * anonymized owner username. Ownership + edit rights still flow through
   * `user_id`; this is display-only.
   */
  presented_as?: string | null;
  /**
   * Phase A: visibility flag toggled by the owner via PATCH /alerts/{id}/visibility.
   * - `true`  → discoverable by other users' agents (find_automations owner=public)
   *             and viewable in read-only sidebar agent on /alerts/{id}.
   * - `false` → only the owner's own agent + REST endpoints can see it.
   * Curated "influencer" alerts (presented_as + publish_status=published)
   * were backfilled to `true` by the migration script. New alerts default
   * to `false` and the owner explicitly opts in via the More menu.
   */
  is_public?: boolean;
  created_at?: string;
  updated_at?: string;
  paused_at?: string;
  resumed_at?: string;
  last_run_at?: string | null;
  last_run_status?: string | null;
  total_runs?: number;
  total_alerts_sent?: number;
  cell_indices?: number[];
  timezone?: string;
  explanation_md?: string | null;
  last_alert_url?: string | null;
  /**
   * Per-cell markdown descriptions keyed by fork cell index (as strings).
   * Generated once at upload time (`_upload_alert` in the backend) via
   * `explain_cell` and stored on the alert doc so the frontend can
   * render the rich "Summary" tab on each canvas node without hitting
   * the LLM on every page load.
   *
   * - Keys: string ints ("1", "2", …) because MongoDB forbids int keys.
   * - Cell 0 (setup) never gets a description — key "0" is absent.
   * - For legacy alerts created before this feature, the GET /alerts
   *   endpoint lazily backfills in the background; descriptions appear
   *   on the NEXT page load.
   * - Each value is markdown with "**What it does**" / "**What it
   *   produces**" sections (see backend `alert_explainer._CELL_SYSTEM`).
   */
  cell_descriptions?: Record<string, string> | null;
  /**
   * Backend sets these after each successful PKA run (Loop 3 / Loop 4).
   * They live on the alert doc so the detail response doesn't need a
   * second collection lookup. `null` until the first dashboard snapshot
   * is captured. See persistent_kernel_api/v2/services/alert_executor.py
   * :: _capture_dashboard_snapshot.
   */
  has_snapshots?: boolean;
  first_snapshot_id?: string | null;
  latest_snapshot_id?: string | null;
  /**
   * Set by GET /alerts/subscribed for rows the current user SUBSCRIBED to
   * (vs alerts they own, from GET /alerts). The subscribed projection is
   * minimal — no `schedule`/`notification_email`/owner PII — and these two
   * flags let the sidebar render the row read-only (unsubscribe instead of
   * pause/delete). Absent on the owned-alerts list.
   */
  is_subscribed?: boolean;
  is_owner?: boolean;
  [key: string]: any;
}

/**
 * Public byline for an alert — resolved server-side and returned on every
 * `GET /alerts/{id}` response. Two discriminated variants:
 *
 *   - `creator`:   Curated influencer profile looked up from the `creators`
 *                  collection via `alert.presented_as`. Has real name,
 *                  avatar, X handle, followers, verified badge, optional bio
 *                  and socials. This is the "publish under their name" case.
 *
 *   - `anonymous`: Deterministic pseudo-identity derived from the alert's
 *                  owner Firebase UID (e.g. "SwiftTrader1234" + a 1-of-52
 *                  static SVG avatar). Same scheme the Community feed uses,
 *                  so the visual language is consistent across the product.
 *
 * The frontend chooses what to render (creator byline strip, anonymous pill,
 * or nothing at all) based on `kind` + whether the viewer is the owner.
 */
export type PublicProfile =
  | {
      kind: "creator";
      slug: string;
      display_name: string;
      handle: string;
      handle_url: string | null;
      avatar_url: string | null;
      followers_count: number | null;
      verified: boolean;
      bio: string | null;
      social: {
        x: string | null;
        youtube: string | null;
        substack: string | null;
        website: string | null;
      };
    }
  | {
      kind: "anonymous";
      username: string;
      avatar_url: string;
    };

/**
 * One entry in the featured-alerts row (Autopilot tab on the home page).
 * Backend shape is flat on purpose — the home page renders this above
 * the fold, so we pay the smallest possible payload per card.
 *
 * Maps 1:1 with GET /alerts/featured in alerts.py. `presented_by.kind`
 * is always "creator" for this route (anonymous fallbacks are filtered
 * server-side).
 */
export interface FeaturedAlertItem {
  alert_id: string;
  name: string;
  description: string | null;
  schedule_description: string | null;
  schedule: string | null;
  created_at: string | null;
  subscriber_count: number;
  thumbnail_url: string;
  presented_by: Extract<PublicProfile, { kind: "creator" }>;
}

export interface FeaturedAlertsResponse {
  alerts: FeaturedAlertItem[];
  count: number;
}

/**
 * One public skill from GET /alerts/skills (the Library Skills grid +
 * /skills/{id} detail page). Maps 1:1 with list_public_skills in
 * app/api/routes/alerts.py.
 */
/** Per-model price + runtime estimate for a skill (flash/sonnet/opus). */
export interface SkillModelEstimate {
  est_cost_usd: number;
  est_runtime_seconds?: number;
  measured_raw_usd?: number;
  margin?: number;
  source?: string;
  note?: string;
  uses_sec_agent?: boolean;
}

/** One declared input the Run-now modal should collect before launching. */
export interface SkillInput {
  key: string;
  label: string;
  type?: "text" | "number" | "select" | "textarea";
  required?: boolean;
  placeholder?: string;
  options?: string[];
  help?: string;
  /** Optional prefill so the Run-now modal starts with a sensible value the
   *  user can change. */
  default?: string;
}

export interface PublicSkill {
  id: string;
  name: string;
  is_public: boolean;
  model_tier: "basic" | "max";
  est_cost_usd: number;
  usage_count: number;
  created_at: string | null;
  data_sources: string[];
  agent_instruction: string;
  /** Visible "what this skill does" markdown. Use for previews + subtitles. */
  explanation: string;
  /** Short, bolded one-liner for the title subtitle + card blurb. May be "". */
  description?: string;
  /** Headline runtime estimate, e.g. "~3-4 min". May be null. */
  est_runtime_label?: string | null;
  /** Headline runtime in seconds. May be null. */
  est_runtime_seconds?: number | null;
  /** Real per-model pricing. Keyed by "flash" | "sonnet" | "opus". */
  model_estimates: Record<string, SkillModelEstimate>;
  /** The model pre-selected on launch ("flash" | "sonnet" | "opus"). */
  recommended_model: string | null;
  /** GPT-5.4 thumbnail URL (null until backfilled). Square-ish, for cards. */
  thumbnail_url: string | null;
  /** GPT-5.4 wide hero wallpaper (21:9) for the skill page banner. May be null. */
  banner_url?: string | null;
  /** Optional taller mobile variant of the hero wallpaper. May be null. */
  banner_url_mobile?: string | null;
  /** Curated "Example Runs": real finished sessions of this skill, one per
   *  model, shown on the skill page so users see what a run produces. */
  example_runs?: SkillExampleRun[];
  /** Optional structured inputs for the Run-now modal. */
  inputs: SkillInput[];
  /** Personal-clone bookkeeping: the public id this was cloned from, or null
   *  on public originals + user-authored skills. */
  cloned_from?: string | null;
  /** Owner uid for personal skills; null on public catalog originals. */
  owner?: string | null;
}

/** One curated example session for a skill, shown in the "Example Runs"
 *  section. `model_slug` is the OpenRouter slug (e.g. "anthropic/claude-opus-4.8")
 *  so the frontend can resolve the model's icon + label via agent-models. */
export interface SkillExampleRun {
  session_id: string;
  model_slug: string;
  title: string;
  /** 16:9 preview image (the run's final chart). May be empty. */
  thumbnail_url?: string | null;
}

/**
 * One public signal from GET /alerts/signals (the Library Signals grid).
 * Card-sized projection: headline track-record stats + a downsampled equity
 * curve so the card renders a live mini chart that moves with the signal.
 * Maps 1:1 with list_public_signals in app/api/routes/alerts.py.
 */
export interface PublicSignalPerformance {
  all_time_pct: number;
  /** Capital deployed across all trades (real $). */
  deployed_usd: number;
  /** Total profit in $ (deployed + this = current value). */
  total_pnl_usd: number;
  win_rate: number; // 0..1
  total_trades: number;
  max_drawdown_pct: number;
}

export interface PublicSignalEquityPoint {
  date: string | null;
  value: number;
}

export interface PublicSignalFeaturedPosition {
  name: string;
  ticker: string;
  direction: "long" | "short";
  entry: number | null;
  current_price: number | null;
  take_profit: number | null;
  stop: number | null;
  trail_pct: number | null;
  time_exit_days: number | null;
  notional_usd: number | null;
  pnl_pct: number | null;
  opened_at: string | null;
}

export interface PublicSignal {
  id: string;
  name: string;
  kind: "signal";
  created_at: string | null;
  thesis: string;
  performance: PublicSignalPerformance;
  open_trades: number;
  equity_curve: PublicSignalEquityPoint[];
  last_registered_at?: string | null;
  /** Newest open position — powers the card's mini Current-Signal thumbnail. */
  featured_position?: PublicSignalFeaturedPosition | null;
}

export interface PublicSignalsResponse {
  signals: PublicSignal[];
  count: number;
}

export interface PublicSkillsResponse {
  skills: PublicSkill[];
  count: number;
}

/**
 * Top-level shape returned by GET /alerts/{id}. The `alert` object is the
 * canonical doc; the sibling fields are viewer-relative flags the backend
 * computes fresh per request. Frontend should prefer these over reaching
 * into `alert` for the same info.
 */
export interface AlertDetailResponse {
  alert: AlertDoc;
  is_owner: boolean;
  is_subscribed: boolean;
  /** Email the CURRENT caller subscribed with. `null` for owners + strangers. */
  subscribed_email: string | null;
  /** Only present when `is_owner` is true (privacy — hidden from strangers). */
  subscriber_count?: number;
  /** Public byline resolver output. Always present — see PublicProfile. */
  presented_by: PublicProfile;
  has_snapshots: boolean;
  first_snapshot_id: string | null;
  latest_snapshot_id: string | null;
  /**
   * First cell index that belongs to the alert build (linked session). Used
   * by the canvas when the viewer is a non-owner: their chat session id is
   * a sandbox, but the canvas fetches `active-run-details` on the owner
   * `linked_session_id` and must filter with this value.
   */
  alert_start_cell_index?: number | null;
}

/**
 * Metadata entry returned by GET /alerts/{id}/snapshots (list). No HTML
 * payload here — keeping the list lightweight so the Logs modal can
 * render thumbnails without shipping megabytes.
 */
export interface AlertSnapshotMeta {
  id: string;
  alert_id: string;
  notebook_id: string;
  run_at: string;
  is_first: boolean;
  run_status: string;
  executor_meta: Record<string, any>;
  /** Present when the PKA image-gen pipeline succeeded for this run.
   *  The frontend prefers the single-image dashboard when set; absence
   *  means the viewer falls back to the iframe'd HTML (legacy path). */
  image_url?: string | null;
  image_generated_at?: string | null;
  image_model?: string | null;
  image_bytes?: number | null;
}

/** Full snapshot including the rendered dashboard HTML. */
export interface AlertSnapshot extends AlertSnapshotMeta {
  html: string;
  narrative: Record<string, any> | null;
}

export interface LiveSupportRoomResponse {
  status: string;
  meeting_url: string;
  email_sent: boolean;
  requested_at: string;
}

export interface SupportConfigResponse {
  live_enabled: boolean;
  next_available_at: string | null;
}

// Curated Session
export interface CuratedSession {
  curated_id: string;
  title: string;
  description: string;
  thumbnail_url: string | null;
  image_urls: string[];
  difficulty: "beginner" | "intermediate" | "advanced" | "phd";
  themes: string[];
  views: number;
  created_at: string | null;
}

export interface CuratedSessionsResponse {
  sessions: CuratedSession[];
  total: number;
}

// Feed Session
export interface FeedSession {
  session_id: string;
  username: string;
  user_avatar: string;
  user_plan: string;
  user_credits_used: number;
  title: string;
  impression_score: number;
  preview_question: string;
  preview_answer: string;
  message_count: number;
  mode: string;
  created_at: string;
  updated_at: string;
  last_scored_at: string;
  first_user_message: string;
  last_message_preview: string;
  last_image: string | null;
  category: string | null;
}

export interface FeedSessionsResponse {
  sessions: FeedSession[];
  total: number;
  min_score_threshold: number;
}

// Affiliate
export interface AffiliateInfo {
  partner_id: string | null;
  referral_code: string | null;
  referral_link: string | null;
  total_earned: number | null;
  total_referrals: number | null;
  pending_amount: number | null;
  enrolled: boolean;
}

// Stripe Checkout
export interface CreateCheckoutSessionRequest {
  user_id?: string;
  tier: string;
  is_trial?: boolean;
  billing_interval?: 'month' | 'year';
  success_url: string;
  cancel_url: string;
}

export interface CreateCheckoutSessionResponse {
  checkout_url: string;
}

/** A Plotly figure as returned by plotly.io.to_json (data + layout). */
export interface PlotlyFigureJSON {
  data: Array<Record<string, unknown>>;
  layout?: Record<string, unknown>;
  config?: Record<string, unknown>;
}

export interface OptionsPnlStrategyMetrics {
  net_cost: number;
  max_profit: number;
  max_loss: number;
  probability_of_profit: number;
  breakeven_prices: number[];
  expiry_date: string;
  expected_profit: number;
  avg_implied_volatility: number;
}

export interface OptionsPnlAnalysis {
  ok: boolean;
  spot: number;
  elapsed_ms: number;
  unified_title?: string;
  strategy_metrics?: OptionsPnlStrategyMetrics;
  pnl_diagram_figure?: PlotlyFigureJSON | null;
  heatmap_figure?: PlotlyFigureJSON | null;
}

export interface OptionsPnlSnapshotLeg {
  leg: Record<string, unknown>;
  entry?: number | null;
  exit?: number | null;
  entry_source?: string | null;
  exit_source?: string | null;
  entry_bid?: number | null;
  entry_ask?: number | null;
  exit_bid?: number | null;
  exit_ask?: number | null;
  entry_error?: string | null;
  exit_error?: string | null;
}

export interface OptionsPnlSnapshot {
  ok: boolean;
  entry?: number | null;
  exit?: number | null;
  pnl_pct?: number | null;
  pnl_usd?: number | null;
  entry_at?: string;
  exit_at?: string;
  legs?: OptionsPnlSnapshotLeg[];
  elapsed_ms?: number;
}


// ─────────────────────────────────────────────────────────────────────────
// Feedback board — public read, Firebase-authed write. Shapes match
// xynth-backend/app/api/routes/feedback_board.py exactly.
// ─────────────────────────────────────────────────────────────────────────
export type FeedbackPostType = "feature" | "bug" | "idea";
export type FeedbackPostStatus = "open" | "planned" | "shipped" | "resolved";

export interface FeedbackAuthor {
  name: string;
  avatar?: string | null;
}

export interface FeedbackPost {
  id: string;
  title: string;
  body: string;
  type: FeedbackPostType;
  status: FeedbackPostStatus;
  upvotes: number;
  comment_count: number;
  author: FeedbackAuthor;
  created_at: string;
  has_voted: boolean;
}

export interface FeedbackComment {
  id: string;
  body: string;
  author: FeedbackAuthor;
  created_at: string;
}

export interface FeedbackPostDetail extends FeedbackPost {
  comments: FeedbackComment[];
}

export interface FeedbackPostsResponse {
  posts: FeedbackPost[];
  total: number;
}

export interface FeedbackVoteResponse {
  post_id: string;
  upvotes: number;
  has_voted: boolean;
}

// ─────────────────────────────────────────────────────────────────────────
// Changelog / "Updates" — public, read-only product update feed. Shapes match
// xynth-backend/app/api/routes/changelog.py exactly.
// ─────────────────────────────────────────────────────────────────────────
export type ChangelogCategory = "new" | "improved" | "fixed";

export interface ChangelogEntry {
  id: string;
  title: string;
  body: string;
  category: ChangelogCategory;
  area?: string | null;
  shipped_at: string;
}

export interface ChangelogEntriesResponse {
  entries: ChangelogEntry[];
  total: number;
}

export interface ChangelogReleaseHighlight {
  title: string;
  body: string;
}

export interface ChangelogRelease {
  id: string;
  version: string;
  name: string;
  summary: string;
  highlights: ChangelogReleaseHighlight[];
  released_at: string;
  /** Optional featured video shown as a row inside the release. */
  video_url?: string | null;
  video_thumbnail_url?: string | null;
  video_caption?: string | null;
}

export interface ChangelogReleasesResponse {
  releases: ChangelogRelease[];
  total: number;
}

class APIClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  /**
   * Get a session by session ID
   * Returns raw data from backend without any transformations
   * @param sessionId - The session ID to fetch
   * @param requestingUserId - Optional user ID to check ownership and set read_only flag
   */
  async getSession(sessionId: string, requestingUserId?: string): Promise<SessionWithMessages> {
    const url = new URL(`${this.baseUrl}/sessions/${sessionId}`);
    if (requestingUserId) {
      url.searchParams.append('requesting_user_id', requestingUserId);
    }
    const response = await fetch(url.toString());
    if (!response.ok) {
      throw new Error("Failed to fetch");
    }
    return response.json();
  }

  /**
   * Update a session by session ID
   */
  async updateSession(
    sessionId: string,
    updateData: SessionUpdateRequest
  ): Promise<SessionResponse> {
    const response = await fetch(`${this.baseUrl}/sessions/${sessionId}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(updateData),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to update session: ${response.status} ${errorText}`
      );
    }

    return response.json();
  }

  /**
   * Update session visibility specifically
   */
  async updateSessionVisibility(
    sessionId: string,
    visibility: "private" | "public"
  ): Promise<SessionResponse> {
    return this.updateSession(sessionId, { visibility });
  }

  /**
   * Delete an uploaded file
   */
  async deleteFile(filePath: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/upload/file/${filePath}`, {
      method: "DELETE",
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to delete file: ${response.status} ${errorText}`);
    }
  }

  /**
   * Delete an ingested document by its docId
   */
  async deleteIngestDoc(docId: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/upload/ingest/${docId}`, {
      method: "DELETE",
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to delete ingested document: ${response.status} ${errorText}`
      );
    }
  }

  /**
   * Get votes for a session
   */
  async getVotes(sessionId: string): Promise<Vote[]> {
    const response = await fetch(
      `${this.baseUrl}/api/vote?session_id=${sessionId}`
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch votes: ${response.status} ${errorText}`);
    }

    const backendVotes: BackendVote[] = await response.json();

    // Transform backend format to frontend format
    return backendVotes.map((vote) => ({
      chatId: vote.session_id,
      messageId: vote.messageId,
      isUpvoted: vote.isUpvoted,
    }));
  }

  /**
   * Vote on a message
   */
  async voteMessage(
    sessionId: string,
    messageId: string,
    type: "up" | "down"
  ): Promise<VoteResponse> {
    const response = await fetch(`${this.baseUrl}/api/vote`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        session_id: sessionId,
        messageId,
        type,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to vote on message: ${response.status} ${errorText}`
      );
    }

    return response.json();
  }

  /**
   * Get Firebase auth token for API requests
   */
  private async getAuthHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    try {
      const user = auth.currentUser;
      if (user) {
        const token = await user.getIdToken();
        headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
    }

    return headers;
  }

  /**
   * Create Stripe Checkout session and return checkout_url
   */
  async createCheckoutSession(
    data: CreateCheckoutSessionRequest
  ): Promise<CreateCheckoutSessionResponse> {
    const headers = await this.getAuthHeaders();
    // Prefer explicit user_id, fallback to Firebase auth
    const explicitUserId = data.user_id;
    const user = auth.currentUser;
    const userId = explicitUserId || user?.uid;

    if (!userId) {
      throw new Error("User not authenticated");
    }

    const payload = {
      user_id: userId,
      tier: data.tier,
      is_trial: data.is_trial || false,
      billing_interval: data.billing_interval,
      success_url: data.success_url,
      cancel_url: data.cancel_url,
      tolt_referral: (window as any).tolt_referral,
    };

    const response = await fetch(
      `${this.baseUrl}/subscriptions/create-checkout-session`,
      {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to create checkout session: ${response.status} ${errorText}`
      );
    }

    const json = (await response.json()) as CreateCheckoutSessionResponse;
    if (!json?.checkout_url) {
      throw new Error("Invalid checkout session response");
    }
    return json;
  }

  /**
   * Purchase additional usage balance (legacy endpoint name kept for compatibility).
   */
  async purchaseCredits(
    amountDollars: number,
    successUrl?: string,
    cancelUrl?: string,
  ): Promise<{ checkout_url: string }> {
    const headers = await this.getAuthHeaders();
    const user = auth.currentUser;
    if (!user) throw new Error("User not authenticated");

    const response = await fetch(
      `${this.baseUrl}/subscriptions/purchase-credits`,
      {
        method: "POST",
        headers,
        body: JSON.stringify({
          user_id: user.uid,
          amount_dollars: amountDollars,
          success_url: successUrl || `${window.location.origin}/`,
          cancel_url: cancelUrl || `${window.location.origin}/`,
        }),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to purchase credits: ${response.status} ${errorText}`);
    }

    return response.json();
  }

  async addFunds(
    amountDollars: number,
    successUrl?: string,
    cancelUrl?: string,
  ): Promise<{ checkout_url: string }> {
    return this.purchaseCredits(amountDollars, successUrl, cancelUrl);
  }

  async getPaymentMethods(): Promise<{
    has_saved_method: boolean;
    methods: { id: string; brand: string; last4: string; exp_month: number; exp_year: number }[];
  }> {
    const headers = await this.getAuthHeaders();
    const user = auth.currentUser;
    if (!user) return { has_saved_method: false, methods: [] };
    const response = await fetch(
      `${this.baseUrl}/subscriptions/payment-methods/${user.uid}`,
      { headers },
    );
    if (!response.ok) return { has_saved_method: false, methods: [] };
    return response.json();
  }

  async quickUpgrade(
    tier: string,
  ): Promise<{
    success: boolean;
    tier: string;
    monthly_budget_usd: number;
    subscription_id: string;
  }> {
    const headers = await this.getAuthHeaders();
    const user = auth.currentUser;
    if (!user) throw new Error("User not authenticated");
    const response = await fetch(
      `${this.baseUrl}/subscriptions/quick-upgrade`,
      {
        method: "POST",
        headers,
        body: JSON.stringify({ user_id: user.uid, tier }),
      },
    );
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText);
    }
    return response.json();
  }

  async quickAddFunds(
    amountDollars: number = 10,
  ): Promise<{
    success: boolean;
    amount_dollars: number;
    credits_added: number;
    card_brand: string;
    card_last4: string;
    payment_intent_id: string;
  }> {
    const headers = await this.getAuthHeaders();
    const user = auth.currentUser;
    if (!user) throw new Error("User not authenticated");
    const response = await fetch(
      `${this.baseUrl}/subscriptions/quick-add-funds`,
      {
        method: "POST",
        headers,
        body: JSON.stringify({ user_id: user.uid, amount_dollars: amountDollars }),
      },
    );
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText);
    }
    return response.json();
  }

  /**
   * Activate subscription immediately (end trial early, start billing)
   */
  async activateSubscription(): Promise<{ success: boolean; message: string }> {
    const headers = await this.getAuthHeaders();
    const user = auth.currentUser;
    if (!user) throw new Error("User not authenticated");

    const response = await fetch(
      `${this.baseUrl}/subscriptions/activate-subscription`,
      {
        method: "POST",
        headers,
        body: JSON.stringify({ user_id: user.uid }),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to activate subscription: ${response.status} ${errorText}`);
    }

    return response.json();
  }

  /**
   * Check if a user is eligible for a free trial
   */
  async checkTrialEligibility(
    userId: string
  ): Promise<{ eligible: boolean; reason: string; code?: string; retry_at?: string | null }> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(
      `${this.baseUrl}/subscriptions/trial-eligibility/${userId}`,
      { method: "GET", headers }
    );
    if (!response.ok) {
      return { eligible: false, reason: "Failed to check eligibility" };
    }
    return response.json();
  }

  /**
   * Cancel an active free trial
   */
  async cancelTrial(userId: string, reason?: string, feedback?: string): Promise<{ success: boolean; message: string }> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(
      `${this.baseUrl}/subscriptions/cancel-trial`,
      {
        method: "POST",
        headers,
        body: JSON.stringify({ user_id: userId, reason, feedback }),
      }
    );
    if (!response.ok) {
      const errorText = await response.text();
      return { success: false, message: errorText };
    }
    return response.json();
  }

  /**
   * Cancel an active paid subscription (sets cancel_at_period_end)
   */
  async cancelSubscription(userId: string, reason?: string, feedback?: string): Promise<{ success: boolean; message: string }> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(
      `${this.baseUrl}/subscriptions/cancel-subscription`,
      {
        method: "POST",
        headers,
        body: JSON.stringify({ user_id: userId, reason, feedback }),
      }
    );
    if (!response.ok) {
      const errorText = await response.text();
      return { success: false, message: errorText };
    }
    return response.json();
  }

  /**
   * Accept a retention save offer from the cancellation review flow.
   * Backend applies the side effect (Stripe pause / coupon / credits grant)
   * and writes a `save_offers_used` record (90-day abuse window).
   */
  async applyCancellationSaveOffer(
    userId: string,
    offerType: "downgrade" | "credits",
    reason?: string,
    feedback?: string,
  ): Promise<{ success: boolean; offer_type?: string; stripe_artifact?: string | null; message: string }> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(
      `${this.baseUrl}/subscriptions/cancellation/save-offer`,
      {
        method: "POST",
        headers,
        body: JSON.stringify({
          user_id: userId,
          offer_type: offerType,
          reason,
          feedback,
        }),
      },
    );
    if (!response.ok) {
      const errorText = await response.text();
      return { success: false, message: errorText };
    }
    return response.json();
  }

  /**
   * Reactivate a subscription that's pending cancellation (undo cancel_at_period_end)
   */
  async reactivateSubscription(userId: string): Promise<{ success: boolean; message: string }> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(
      `${this.baseUrl}/subscriptions/reactivate-subscription`,
      {
        method: "POST",
        headers,
        body: JSON.stringify({ user_id: userId }),
      }
    );
    if (!response.ok) {
      const errorText = await response.text();
      return { success: false, message: errorText };
    }
    return response.json();
  }

  /**
   * Save subscriber declaration (non-professional / professional) before checkout
   */
  async saveSubscriberDeclaration(
    userId: string,
    status: 'non-professional' | 'professional',
    fullName: string
  ): Promise<{ success: boolean }> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(
      `${this.baseUrl}/subscriptions/subscriber-declaration`,
      {
        method: "POST",
        headers,
        body: JSON.stringify({ user_id: userId, status, full_name: fullName }),
      }
    );
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to save declaration: ${response.status} ${errorText}`);
    }
    return response.json();
  }

  /**
   * Generic GET method with Firebase auth
   */
  async get<T>(endpoint: string): Promise<T> {
    const headers = await this.getAuthHeaders();

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      throw await buildApiError(response, `Failed to GET ${endpoint}`);
    }

    // Handle non-JSON responses gracefully
    const contentType = response.headers.get("content-type") || "";
    
    // If no content (204) or empty body, return null
    if (response.status === 204 || !contentType) {
      return null as T;
    }

    // If response is JSON, parse it
    if (contentType.includes("application/json")) {
      const data = await response.json();
      return data;
    }

    // Otherwise return text (for markdown, plain text, etc.)
    const text = await response.text();
    return text as T;
  }

  /**
   * Generic POST method for auth endpoints
   */
  async post(endpoint: string, data: any): Promise<any> {
    const headers = await this.getAuthHeaders();

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: "POST",
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw await buildApiError(response, `Failed to ${endpoint}`);
    }

    return response.json();
  }

  /**
   * Generic PATCH method with Firebase auth.
   * Used by the alerts management page (rename, update email, schedule, etc.).
   */
  async patch<T = any>(endpoint: string, data: any): Promise<T> {
    const headers = await this.getAuthHeaders();

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: "PATCH",
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw await buildApiError(response, `Failed to PATCH ${endpoint}`);
    }

    return response.json();
  }

  /**
   * Generic DELETE method with Firebase auth.
   * Used by the alerts management page for cascade-delete.
   */
  async delete<T = any>(endpoint: string): Promise<T> {
    const headers = await this.getAuthHeaders();

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: "DELETE",
      headers,
    });

    if (!response.ok) {
      throw await buildApiError(response, `Failed to DELETE ${endpoint}`);
    }

    return response.json();
  }

  // ───────────────────────────────────────────────────────────────────────
  // Feedback board — backs the /feedback page. Reads are public (the Firebase
  // token is sent when available so `has_voted` is accurate); writes require
  // login. Endpoints match xynth-backend feedback_board.py.
  // ───────────────────────────────────────────────────────────────────────
  async listFeedbackPosts(params?: {
    sort?: "top" | "new";
    status?: FeedbackPostStatus;
    limit?: number;
    skip?: number;
  }): Promise<FeedbackPostsResponse> {
    const q = new URLSearchParams();
    if (params?.sort) q.set("sort", params.sort);
    if (params?.status) q.set("status", params.status);
    if (params?.limit != null) q.set("limit", String(params.limit));
    if (params?.skip != null) q.set("skip", String(params.skip));
    const qs = q.toString();
    return this.get<FeedbackPostsResponse>(
      `/feedback-board/posts${qs ? `?${qs}` : ""}`,
    );
  }

  async getFeedbackPost(postId: string): Promise<FeedbackPostDetail> {
    return this.get<FeedbackPostDetail>(`/feedback-board/posts/${postId}`);
  }

  async createFeedbackPost(data: {
    title: string;
    body?: string;
    type?: FeedbackPostType;
  }): Promise<FeedbackPost> {
    return this.post(`/feedback-board/posts`, data);
  }

  async toggleFeedbackVote(postId: string): Promise<FeedbackVoteResponse> {
    return this.post(`/feedback-board/posts/${postId}/vote`, {});
  }

  async addFeedbackComment(
    postId: string,
    body: string,
  ): Promise<FeedbackComment> {
    return this.post(`/feedback-board/posts/${postId}/comments`, { body });
  }

  // ───────────────────────────────────────────────────────────────────────
  // Changelog / "Updates" — public read. Backs the /updates tab. No auth.
  // Endpoint matches xynth-backend changelog.py.
  // ───────────────────────────────────────────────────────────────────────
  async listChangelogEntries(params?: {
    category?: ChangelogCategory;
    limit?: number;
    skip?: number;
  }): Promise<ChangelogEntriesResponse> {
    const q = new URLSearchParams();
    if (params?.category) q.set("category", params.category);
    if (params?.limit != null) q.set("limit", String(params.limit));
    if (params?.skip != null) q.set("skip", String(params.skip));
    const qs = q.toString();
    return this.get<ChangelogEntriesResponse>(
      `/changelog/entries${qs ? `?${qs}` : ""}`,
    );
  }

  /** Versioned releases (Xynth v1.0, v2.0, ...) shown on the /updates tab. */
  async listChangelogReleases(params?: {
    limit?: number;
    skip?: number;
  }): Promise<ChangelogReleasesResponse> {
    const q = new URLSearchParams();
    if (params?.limit != null) q.set("limit", String(params.limit));
    if (params?.skip != null) q.set("skip", String(params.skip));
    const qs = q.toString();
    return this.get<ChangelogReleasesResponse>(
      `/changelog/releases${qs ? `?${qs}` : ""}`,
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Alert management — Firebase-authed REST endpoints backing the /alerts/{id}
  // page. Shapes match xynth-backend/app/api/routes/alerts.py exactly.
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Fetch a single alert + the viewer's relationship to it.
   *
   * The backend enriches the response based on who's asking:
   *   - `is_owner` — caller owns the alert
   *   - `is_subscribed` — caller is an active subscriber (non-owner only)
   *   - `subscribed_email` — email they subscribed with (non-owner only)
   *   - `subscriber_count` — ONLY present when caller is owner (privacy)
   *   - `has_snapshots` — true once the alert has captured a dashboard
   *   - `first_snapshot_id` / `latest_snapshot_id` — pointers for the
   *     DELAYED / LIVE canvas views
   *
   * Backend: xynth-backend/app/api/routes/alerts.py::get_alert
   */
  async getAlert(alertId: string): Promise<AlertDetailResponse> {
    return this.get<AlertDetailResponse>(`/alerts/${alertId}`);
  }

  /**
   * Plain-English "What this alert does" briefing for the Description tab.
   * Lazily (re)generates with a vision model server-side when stale/missing,
   * then persists — so the next viewer gets it instantly. Pass force=true to
   * always regenerate (manual backfill of a specific alert).
   */
  async getAlertExplanation(
    alertId: string,
    opts?: { force?: boolean },
  ): Promise<{
    explanation_md: string;
    version: string | null;
    had_image: boolean;
    regenerated: boolean;
  }> {
    const qs = opts?.force ? "?force=true" : "";
    return this.get(`/alerts/${alertId}/explanation${qs}`);
  }

  /**
   * List all non-deleted alerts for the current Firebase-authed user,
   * newest first. Backend route: GET /alerts (see
   * xynth-backend/app/api/routes/alerts.py::list_alerts).
   */
  async listAlerts(): Promise<{ alerts: AlertDoc[]; count: number }> {
    return this.get<{ alerts: AlertDoc[]; count: number }>(`/alerts`);
  }

  /**
   * List alerts the current user has an ACTIVE subscription to (separate
   * from alerts they own). Powers the "Subscribed" group in the sidebar.
   * Each row is a minimal projection with `is_owner: false` and
   * `is_subscribed: true` — no owner PII. Backend route:
   * GET /alerts/subscribed (xynth-backend/app/api/routes/alerts.py::list_subscribed_alerts).
   */
  async listSubscribedAlerts(): Promise<{ alerts: AlertDoc[]; count: number }> {
    return this.get<{ alerts: AlertDoc[]; count: number }>(`/alerts/subscribed`);
  }

  async updateAlertName(alertId: string, name: string) {
    return this.patch(`/alerts/${alertId}/name`, { name });
  }

  async updateAlertDescription(alertId: string, description: string) {
    return this.patch(`/alerts/${alertId}/description`, { description });
  }

  async updateAlertEmail(alertId: string, notification_email: string) {
    return this.patch(`/alerts/${alertId}/notification-email`, { notification_email });
  }

  async updateAlertSchedule(
    alertId: string,
    payload: { schedule: string; schedule_description?: string },
  ) {
    return this.patch(`/alerts/${alertId}/schedule`, payload);
  }

  /**
   * Parse a plain-English schedule description into a validated 5-field cron
   * expression via Gemini. READ-ONLY: this does NOT update any alert — caller
   * still has to call updateAlertSchedule() with the returned cron.
   *
   * Returns { ok, cron, schedule_description, confidence, interval_seconds, error }.
   * When ok=false, `error` is already user-safe for direct display.
   */
  async parseAlertSchedule(payload: {
    text: string;
    session_id?: string;
  }): Promise<{
    ok: boolean;
    cron: string | null;
    schedule_description: string | null;
    confidence: "high" | "medium" | "low" | null;
    interval_seconds: number | null;
    error: string | null;
  }> {
    return this.post(`/alerts/parse-schedule`, payload);
  }

  async pauseAlert(alertId: string) {
    return this.post(`/alerts/${alertId}/pause`, {});
  }

  async resumeAlert(alertId: string) {
    return this.post(`/alerts/${alertId}/resume`, {});
  }

  /**
   * Toggle the alert's `is_public` visibility flag. Owner-only on the
   * backend (404s if caller doesn't own the alert). Returns the resulting
   * is_public value plus a `noop: true` flag when the flip was redundant.
   */
  async updateAlertVisibility(alertId: string, is_public: boolean) {
    return this.patch(`/alerts/${alertId}/visibility`, { is_public });
  }

  async runAlertNow(alertId: string) {
    return this.post(`/alerts/${alertId}/run-now`, {});
  }

  async deleteAlert(alertId: string) {
    return this.delete(`/alerts/${alertId}`);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Subscribe / Unsubscribe — Loop 1 (public subscribe feature)
  //
  // Any Firebase-authed user on a paid Xynth tier can subscribe to any
  // alert by ID. Free-tier callers get HTTP 402; the frontend switches on
  // that to open the pricing modal. Owners cannot subscribe to their own
  // alerts (backend returns 400).
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Subscribe the current caller to receive emails when this alert fires.
   * Uses the Firebase user's email by default; pass `email` to override.
   * Backend: POST /alerts/{id}/subscribe.
   */
  async subscribeAlert(
    alertId: string,
    email?: string,
  ): Promise<{ status: string; is_subscribed: boolean; email: string }> {
    return this.post(`/alerts/${alertId}/subscribe`, email ? { email } : {});
  }

  /**
   * Unsubscribe the current caller. Idempotent — safe to call even if
   * they weren't actively subscribed. Backend: POST /alerts/{id}/unsubscribe.
   */
  async unsubscribeAlert(
    alertId: string,
  ): Promise<{ status: string; is_subscribed: boolean }> {
    return this.post(`/alerts/${alertId}/unsubscribe`, {});
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Dashboard snapshots — Loops 3 & 4 (history + DELAYED/LIVE views)
  //
  // Four endpoints with different auth:
  //   - list           owner only — powers the Logs modal
  //   - get-specific   owner only — click a row in the Logs modal
  //   - get-first      PUBLIC     — powers the DELAYED view for strangers
  //   - get-latest     owner OR active subscriber — powers the LIVE view
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Owner-only — list up to 30 past snapshots (newest first). NO HTML in
   * the response; use `getAlertSnapshot(id)` to fetch full HTML when the
   * user clicks a row.
   */
  async getAlertSnapshotsList(
    alertId: string,
  ): Promise<{ snapshots: AlertSnapshotMeta[]; count: number }> {
    return this.get(`/alerts/${alertId}/snapshots`);
  }

  /** Owner-only — fetch a specific snapshot's full HTML. */
  async getAlertSnapshot(
    alertId: string,
    snapshotId: string,
  ): Promise<{ snapshot: AlertSnapshot }> {
    return this.get(`/alerts/${alertId}/snapshots/${snapshotId}`);
  }

  /**
   * PUBLIC (no auth) — first-ever snapshot. The frontend uses this as the
   * DELAYED-view anchor for strangers / free users who haven't subscribed.
   */
  async getAlertSnapshotFirst(
    alertId: string,
  ): Promise<{ snapshot: AlertSnapshot }> {
    return snapshotBodies.read(
      `${this.baseUrl}:${alertId}:first`,
      () => auth.currentUser?.uid || "anonymous",
      (knownId) => this.get(`/alerts/${alertId}/snapshots/first${knownId ? `?known_snapshot_id=${encodeURIComponent(knownId)}` : ""}`),
    );
  }

  /**
   * Owner OR active subscriber — freshest snapshot. The frontend uses this
   * as the LIVE-view anchor. Non-subscribed callers receive HTTP 403 with
   * a structured body so the paywall modal can switch on `is_authenticated`.
   */
  async getAlertSnapshotLatest(
    alertId: string,
  ): Promise<{ snapshot: AlertSnapshot }> {
    return snapshotBodies.read(
      `${this.baseUrl}:${alertId}:latest`,
      () => auth.currentUser?.uid || "anonymous",
      (knownId) => this.get(`/alerts/${alertId}/snapshots/latest${knownId ? `?known_snapshot_id=${encodeURIComponent(knownId)}` : ""}`),
    );
  }

  /**
   * Signal track record for kind="signal" alerts — shaped 1:1 to the
   * `SignalPerformance` type used by SignalPerformanceView. Returns 404
   * for non-signal alerts (the dashboard falls back to the HTML snapshot).
   * Backend: xynth-backend/app/api/routes/alerts.py::get_signal_performance
   */
  async getSignalPerformance(alertId: string): Promise<unknown> {
    return this.get(`/alerts/${alertId}/signal-performance`);
  }

  /**
   * Option-strategy P&L analysis (frontend → backend → PKA optionlab engine).
   * Returns two Plotly figures (P&L diagram with a date slider + a multi-expiry
   * heatmap) plus strategy metrics (net cost, max P/L, POP, breakevens).
   * Pass either `stock_price` or `underlying` (PKA fetches the spot).
   */
  async getOptionsPnlAnalysis(payload: {
    legs: Array<Record<string, unknown>>;
    stock_price?: number;
    underlying?: string;
    title?: string;
    // Entry date (ISO) for CLOSED trades so the engine simulates from open
    // instead of refusing the already-expired strategy.
    as_of?: string;
  }): Promise<OptionsPnlAnalysis> {
    const t0 = (typeof performance !== "undefined" ? performance.now() : Date.now());
    console.info("[options-pnl] request", {
      legs: payload.legs?.length, underlying: payload.underlying, as_of: payload.as_of,
    });
    const response = await fetch(`${this.baseUrl}/alerts/options-pnl`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const ms = Math.round((typeof performance !== "undefined" ? performance.now() : Date.now()) - t0);
    if (!response.ok) {
      const err = await buildApiError(response, "Options P&L analysis failed");
      console.warn("[options-pnl] failed", { status: response.status, ms, as_of: payload.as_of, message: err.message });
      throw err;
    }
    console.info("[options-pnl] ok", { ms, as_of: payload.as_of });
    return response.json();
  }

  /**
   * Historical option P&L snapshot for Previous Trades. Re-marks legs at the
   * entry/exit timestamps via backend → PKA → Polygon historical quotes.
   */
  async getOptionsPnlSnapshot(payload: {
    legs: Array<Record<string, unknown>>;
    entry_at: string;
    exit_at?: string;
    direction?: string;
    is_credit?: boolean;
    notional_usd?: number;
  }): Promise<OptionsPnlSnapshot> {
    const response = await fetch(`${this.baseUrl}/alerts/options-pnl-snapshot`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw await buildApiError(response, "Options P&L snapshot failed");
    }
    return response.json();
  }

  /**
   * Start a strategy run
   */
  async startStrategyRun(payload: {
    id: string;
    user_id: string;
    session_id: string;
    mode: string;
    message: string;
  }): Promise<{ status: string; session_id: string; message: string }> {
    const response = await fetch(`${this.baseUrl}/agent/execute`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to start strategy run: ${response.status} ${errorText}`
      );
    }

    return response.json();
  }

  /**
   * Set session status to idle
   */
  async setSessionIdle(sessionId: string): Promise<SessionResponse> {
    const response = await fetch(
      `${this.baseUrl}/sessions/${sessionId}/status?status=idle`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to set session idle: ${response.status} ${errorText}`
      );
    }

    return response.json();
  }

  /**
   * Send context refresh with screenshot and viewport data
   */
  async sendContextRefresh(payload: {
    session_id: string;
    screenshot: string | null;
    viewport: any;
  }): Promise<{ status: string; message: string }> {
    // Get user ID from Firebase auth
    const headers = await this.getAuthHeaders();
    const user = auth.currentUser;
    const userId = user?.uid || "anonymous";

    // Transform payload to match backend expectations
    const backendPayload = {
      user_id: userId,
      session_id: payload.session_id,
      value: {
        type: "context_refresh",
        screenshot: payload.screenshot,
        viewport: payload.viewport,
      },
    };

    const response = await fetch(`${this.baseUrl}/agent/pt-input`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(backendPayload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to send context refresh: ${response.status} ${errorText}`
      );
    }

    return response.json();
  }



  /**
   * Download notebook resource by type (summary | data | code)
   */
  async downloadNotebook(
    notebookId: string,
    type: "summary" | "data" | "code"
  ): Promise<{ blob: Blob; filename?: string; contentType?: string }> {
    const headers = await this.getAuthHeaders();

    const url = new URL(`${this.baseUrl}/download/${notebookId}`);
    url.searchParams.append("type", type);

    const response = await fetch(url.toString(), {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to download ${type}: ${response.status} ${errorText}`
      );
    }

    const blob = await response.blob();
    const contentDisposition = response.headers.get("content-disposition") || "";
    const contentType = response.headers.get("content-type") || undefined;

    // Try to parse filename from Content-Disposition
    let filename: string | undefined = undefined;
    const match = /filename\*?=(?:UTF-8''|\")?([^;\n\"]+)/i.exec(
      contentDisposition
    );
    if (match && match[1]) {
      try {
        filename = decodeURIComponent(match[1].replace(/\"/g, ""));
      } catch {
        filename = match[1].replace(/\"/g, "");
      }
    }

    return { blob, filename, contentType };
  }

  /**
   * Send demo action to backend after fast forward completion
   */
  async sendDemoAction(
    sessionId: string,
    toolCallId: string,
    action: string = "fast_forward"
  ): Promise<{ status: string; message: string }> {
    const response = await fetch(`${this.baseUrl}/demo/demo-action`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        session_id: sessionId,
        tool_call_id: toolCallId,
        action,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to send demo action: ${response.status} ${errorText}`
      );
    }

    return response.json();
  }

  /**
   * Submit a reply for a pending ask_user tool call.
   */
  /**
   * Automations onboarding modal progress.
   * See xynth-backend/app/api/routes/onboarding.py → /alerts-* endpoints.
   */
  async getAlertsOnboardingStatus(
    userId: string,
  ): Promise<{
    max_slide_seen: number;
    total: number;
    completed: boolean;
    should_auto_open: boolean;
  }> {
    const url = new URL(`${this.baseUrl}/onboarding/alerts-status`);
    url.searchParams.set("user_id", userId);
    const response = await fetch(url.toString());
    if (!response.ok) {
      throw new Error(
        `getAlertsOnboardingStatus ${response.status}: ${await response.text()}`,
      );
    }
    return response.json();
  }

  async recordAlertsOnboardingProgress(
    userId: string,
    slideIdx: number,
    completed?: boolean,
  ): Promise<{
    success: boolean;
    max_slide_seen?: number;
    completed?: boolean;
    total?: number;
    reason?: string;
  }> {
    const response = await fetch(
      `${this.baseUrl}/onboarding/alerts-progress`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: userId,
          slide_idx: slideIdx,
          ...(completed !== undefined ? { completed } : {}),
        }),
      },
    );
    if (!response.ok) {
      throw new Error(
        `recordAlertsOnboardingProgress ${response.status}: ${await response.text()}`,
      );
    }
    return response.json();
  }

  async submitAskUserReply(
    sessionId: string,
    toolCallId: string,
    value: string
  ): Promise<{ status: string }> {
    // In replay mode the live `/agent/ask-user-reply` endpoint won't work:
    // it requires a Firebase auth token + a real session in Mongo, neither
    // of which an ephemeral replay session has. Replay sessions instead
    // hit `/replay/ask-user-release` which is auth-free and just releases
    // the gate inside the replay generator (see `_await_ask_user_reply`).
    // We detect replay mode by URL pathname — `/replay/...` is the only
    // route that mounts replay chat sessions.
    const isReplay =
      typeof window !== "undefined" &&
      window.location.pathname.startsWith("/replay/");

    if (isReplay) {
      // Replay path: no auth headers (the endpoint is public), no
      // throw-on-error escalation either since a failed release still
      // auto-releases server-side after BUDDY_GATE_TIMEOUT_SEC.
      const response = await fetch(`${this.baseUrl}/replay/ask-user-release`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId,
          tool_call_id: toolCallId,
          value,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to submit replay ask_user reply: ${response.status} ${errorText}`
        );
      }

      return response.json();
    }

    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.baseUrl}/agent/ask-user-reply`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        session_id: sessionId,
        tool_call_id: toolCallId,
        value,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to submit ask_user reply: ${response.status} ${errorText}`
      );
    }

    return response.json();
  }

  /**
   * Release a pending propose_skill tool call (Create a Skill flow). The
   * waiting backend coroutine BLPOPs this value and the agent continues.
   * value: "__saved__", "__dismissed__", or a plain-English change request.
   */
  async submitProposeSkillReply(
    sessionId: string,
    toolCallId: string,
    value: string,
  ): Promise<{ status: string }> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.baseUrl}/agent/propose-skill-reply`, {
      method: "POST",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify({
        session_id: sessionId,
        tool_call_id: toolCallId,
        value,
      }),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to submit propose_skill reply: ${response.status} ${errorText}`,
      );
    }
    return response.json();
  }

  /**
   * Confirm or cancel a tool call that requires user approval
   * (upload_alert, delete_alert, etc.).
   */
  async submitToolConfirm(
    sessionId: string,
    confirmationId: string,
    decision: "confirm" | "cancel"
  ): Promise<{ status: string; decision: string }> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.baseUrl}/agent/tool-confirm`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        session_id: sessionId,
        confirmation_id: confirmationId,
        decision,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to submit tool confirmation: ${response.status} ${errorText}`
      );
    }

    return response.json();
  }

  /**
   * Mid-flight cancel for `upload_alert` AFTER the user has clicked Publish.
   *
   * Pre-publish cancel uses `submitToolConfirm(..., "cancel")` via the
   * normal tool-confirm card — do NOT use this method for that case.
   *
   * On the backend this writes a Redis flag keyed by the tool_call_id; the
   * running `_upload_alert` coroutine polls the flag between stages (fork →
   * alert doc → linked session → first-run → scheduler). The first checkpoint
   * that sees the flag triggers `delete_alert_cascade` to roll back
   * everything and return a `cancelled` result to the agent.
   */
  async cancelUploadAlert(
    sessionId: string,
    toolCallId: string,
  ): Promise<{ status: string; tool_call_id: string }> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.baseUrl}/agent/upload-alert-cancel`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        session_id: sessionId,
        tool_call_id: toolCallId,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to cancel upload_alert: ${response.status} ${errorText}`,
      );
    }

    return response.json();
  }

  /**
   * Fetch a pending tool confirmation preview (alert details etc.) if the
   * card loads after the tool call started streaming. Backend stores it in
   * Redis for ~90s.
   */
  async getToolConfirmPreview(
    confirmationId: string
  ): Promise<{
    tool: string;
    confirmation_id: string;
    preview: any;
    tool_call_id?: string;
  } | null> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(
      `${this.baseUrl}/agent/tool-confirm-preview/${encodeURIComponent(confirmationId)}`,
      { method: "GET", headers }
    );
    if (!response.ok) return null;
    return response.json();
  }

  /**
   * Stop agent execution for a session
   */
  async stopAgent(
    userId: string,
    sessionId: string,
    reason: string = "user_requested_stop"
  ): Promise<{ status: string; session_id: string }> {
    const response = await fetch(`${this.baseUrl}/agent/stop`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        user_id: userId,
        session_id: sessionId,
        reason,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to stop agent: ${response.status} ${errorText}`
      );
    }

    return response.json();
  }

  /**
   * Submit survey data to backend
   */
  async submitSurvey(surveyData: {
    user_id: string;
    plan_type: string;
    survey_data: {
      howDidYouFindUs: string;
      investorType: string;
      tradingExperience: string;
      primaryGoal: string[];
      expectedFeatures: string[];
      investmentAmount: string;
      tradingFrequency: string;
      mostImportantFeature: string;
      additionalComments: string;
    };
    submitted_at: string;
  }): Promise<{ success: boolean; message?: string; error?: string }> {
    const response = await fetch(`${this.baseUrl}/api/survey`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(surveyData),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to submit survey: ${response.status} ${errorText}`
      );
    }

    return response.json();
  }

  /**
   * Update survey plan type (when user upgrades after completing survey)
   */
  async updateSurveyPlanType(
    userId: string,
    planType: string
  ): Promise<{ success: boolean; message?: string; error?: string }> {
    const response = await fetch(`${this.baseUrl}/api/survey/plan-type`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        user_id: userId,
        plan_type: planType,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to update survey plan type: ${response.status} ${errorText}`
      );
    }

    return response.json();
  }

  /**
   * Login after Firebase authentication - checks verification and auto-sends code if needed
   */
  async login(): Promise<{ verified: boolean }> {
    const headers = await this.getAuthHeaders();

    const response = await fetch(`${this.baseUrl}/auth/login`, {
      method: "POST",
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to login: ${response.status} ${errorText}`
      );
    }

    return response.json();
  }

  /**
   * Send verification code to user's email
   */
  async sendVerificationCode(): Promise<{ success: boolean; message: string }> {
    const headers = await this.getAuthHeaders();

    const response = await fetch(`${this.baseUrl}/auth/send-verification-code`, {
      method: "POST",
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to send verification code: ${response.status} ${errorText}`
      );
    }

    return response.json();
  }

  /**
   * Verify email with code
   */
  async verifyEmailCode(code: string): Promise<{ success: boolean; message: string }> {
    const headers = await this.getAuthHeaders();

    const response = await fetch(`${this.baseUrl}/auth/verify-code`, {
      method: "POST",
      headers,
      body: JSON.stringify({ code }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to verify code: ${response.status} ${errorText}`
      );
    }

    return response.json();
  }

  /**
   * Check whether the authenticated user should still see the mobile hint.
   */
  async getMobileHintStatus(): Promise<MobileHintStatusResponse> {
    return this.get<MobileHintStatusResponse>("/auth/mobile-hint-status");
  }

  /**
   * Permanently dismiss the mobile hint for the authenticated user.
   */
  async dismissMobileHint(): Promise<{ success: boolean; message: string }> {
    return this.post("/auth/mobile-hint-dismiss", {});
  }

  async getNotifyModalStatus(): Promise<{ should_show: boolean }> {
    return this.get<{ should_show: boolean }>("/auth/notify-modal-status");
  }

  async dismissNotifyModal(): Promise<{ success: boolean; message: string }> {
    return this.post("/auth/notify-modal-dismiss", {});
  }

  /**
   * Get top sessions from feed
   */
  async getTopSessions(params?: {
    limit?: number;
    min_score?: number;
    sort_by?: "recent";
    category?: string;
  }): Promise<FeedSessionsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.min_score) queryParams.append("min_score", params.min_score.toString());
    if (params?.sort_by) queryParams.append("sort_by", params.sort_by);
    if (params?.category) queryParams.append("category", params.category);

    const response = await fetch(
      `${this.baseUrl}/feed/top-sessions?${queryParams.toString()}`
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to fetch top sessions: ${response.status} ${errorText}`
      );
    }

    return response.json();
  }

  /**
   * Execute a specific cell in a notebook
   */
  async executeCellByIndex(
    notebookId: string,
    cellIndex: number
  ): Promise<{ status: string; message: string }> {
    const response = await fetch(
      `${this.baseUrl}/notebook/${notebookId}/cells/${cellIndex}/execute`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to execute cell: ${response.status} ${errorText}`
      );
    }

    return response.json();
  }

  /**
   * Featured (Autopilot) alerts for the home page wall. Public endpoint —
   * no auth required, IP rate-limited at 120 req/min. Uses raw `fetch`
   * so logged-out visitors don't trip `getAuthHeaders`.
   *
   * Matches backend route `GET /alerts/featured` in `app/api/routes/alerts.py`.
   * Each entry ships everything the card needs: title, schedule,
   * subscriber count, thumbnail URL, and the resolved creator byline.
   */
  async listFeaturedAlerts(
    params?: { limit?: number },
  ): Promise<FeaturedAlertsResponse> {
    const qs = new URLSearchParams();
    if (params?.limit) qs.append("limit", String(params.limit));
    const url = qs.toString()
      ? `${this.baseUrl}/alerts/featured?${qs.toString()}`
      : `${this.baseUrl}/alerts/featured`;
    const response = await fetch(url);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to fetch featured alerts: ${response.status} ${errorText}`,
      );
    }
    return response.json();
  }

  /**
   * Public list of skills for the Library Skills grid + /skills/{id} page.
   * Unauthenticated (plain fetch, no auth headers) so logged-out visitors
   * can browse. Matches GET /alerts/skills in app/api/routes/alerts.py.
   */
  async listPublicSkills(
    params?: { limit?: number },
  ): Promise<PublicSkillsResponse> {
    const qs = new URLSearchParams();
    if (params?.limit) qs.append("limit", String(params.limit));
    const url = qs.toString()
      ? `${this.baseUrl}/alerts/skills?${qs.toString()}`
      : `${this.baseUrl}/alerts/skills`;
    const response = await fetch(url);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to fetch skills: ${response.status} ${errorText}`,
      );
    }
    return response.json();
  }

  /**
   * Public list of signals for the Library Signals grid. Unauthenticated so
   * logged-out visitors can browse. Matches GET /alerts/signals.
   */
  async listPublicSignals(
    params?: { limit?: number },
  ): Promise<PublicSignalsResponse> {
    const qs = new URLSearchParams();
    if (params?.limit) qs.append("limit", String(params.limit));
    const url = qs.toString()
      ? `${this.baseUrl}/alerts/signals?${qs.toString()}`
      : `${this.baseUrl}/alerts/signals`;
    const response = await fetch(url);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to fetch signals: ${response.status} ${errorText}`,
      );
    }
    return response.json();
  }

  /**
   * The current user's personal skills (their clones). Authed.
   * Matches GET /alerts/skills/mine.
   */
  async listMySkills(): Promise<PublicSkillsResponse> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.baseUrl}/alerts/skills/mine`, {
      headers,
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to fetch my skills: ${response.status} ${errorText}`,
      );
    }
    return response.json();
  }

  /**
   * Clone a public skill into the caller's personal library. Idempotent.
   * Matches POST /alerts/skills/{id}/clone.
   */
  async cloneSkill(
    skillId: string,
  ): Promise<{ skill: PublicSkill; created: boolean }> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(
      `${this.baseUrl}/alerts/skills/${encodeURIComponent(skillId)}/clone`,
      { method: "POST", headers },
    );
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to clone skill: ${response.status} ${errorText}`,
      );
    }
    return response.json();
  }

  /**
   * Create a NEW personal skill from a user-approved draft (the Save action on
   * a proposed-skill card). Authed. Matches POST /alerts/skills. The backend
   * re-validates every field and auto-appends the accuracy guardrails, so the
   * returned skill's agent_instruction may be longer than what we sent.
   */
  async createSkill(payload: {
    name: string;
    description: string;
    explanation: string;
    agent_instruction: string;
    data_sources: string[];
    inputs?: SkillInput[];
    output_type?: string;
    source?: string;
  }): Promise<{ skill: PublicSkill; created: boolean }> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.baseUrl}/alerts/skills`, {
      method: "POST",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to create skill: ${response.status} ${errorText}`,
      );
    }
    return response.json();
  }

  /**
   * Remove a personal skill clone. Owner-only on the backend.
   * Matches DELETE /alerts/skills/{id}.
   */
  async deleteSkill(skillId: string): Promise<{ ok: boolean; deleted: string }> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(
      `${this.baseUrl}/alerts/skills/${encodeURIComponent(skillId)}`,
      { method: "DELETE", headers },
    );
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to delete skill: ${response.status} ${errorText}`,
      );
    }
    return response.json();
  }

  /**
   * Get curated sessions for the feed
   */
  async getCuratedSessions(params?: {
    difficulty?: string;
    theme?: string;
  }): Promise<CuratedSessionsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.difficulty) queryParams.append("difficulty", params.difficulty);
    if (params?.theme) queryParams.append("theme", params.theme);

    const query = queryParams.toString();
    const url = query
      ? `${this.baseUrl}/feed/curated-sessions?${query}`
      : `${this.baseUrl}/feed/curated-sessions`;

    const response = await fetch(url);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to fetch curated sessions: ${response.status} ${errorText}`
      );
    }

    return response.json();
  }

  /**
   * Get current user's affiliate info (referral code, link, earnings)
   */
  async getAffiliateInfo(): Promise<AffiliateInfo> {
    return this.get<AffiliateInfo>("/affiliate/me");
  }

  /**
   * Manually enroll user in affiliate program (for pre-existing users)
   */
  async enrollAffiliate(): Promise<AffiliateInfo> {
    return this.post("/affiliate/enroll", {});
  }

  /**
   * Check running status for multiple sessions in a single call.
   */
  async batchRequestStatus(
    sessionIds: string[]
  ): Promise<{ results: Array<{ session_id: string; status: string }> }> {
    const response = await fetch(
      `${this.baseUrl}/agent/batch-request-status`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_ids: sessionIds }),
      }
    );
    if (!response.ok) return { results: [] };
    return response.json();
  }

  /**
   * Register email notification for session completion.
   */
  async registerNotifyOnComplete(
    sessionId: string,
    userEmail: string,
    targetRequestId?: string
  ): Promise<{ status: string; message: string }> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(
      `${this.baseUrl}/agent/notify-on-complete`,
      {
        method: "POST",
        headers,
        body: JSON.stringify({
          session_id: sessionId,
          user_email: userEmail,
          target_request_id: targetRequestId,
        }),
      }
    );
    if (!response.ok) throw new Error("Failed to register notification");
    return response.json();
  }

  /**
   * Cancel a session completion notification.
   */
  async cancelNotifyOnComplete(sessionId: string): Promise<void> {
    const headers = await this.getAuthHeaders();
    await fetch(
      `${this.baseUrl}/agent/notify-on-complete?session_id=${encodeURIComponent(sessionId)}`,
      {
        method: "DELETE",
        headers,
      }
    );
  }

  /**
   * Check if notification is registered for a session.
   */
  async getNotifyOnComplete(
    sessionId: string
  ): Promise<{ registered: boolean }> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(
      `${this.baseUrl}/agent/notify-on-complete?session_id=${encodeURIComponent(sessionId)}`,
      { headers }
    );
    if (!response.ok) return { registered: false };
    return response.json();
  }

  /**
   * Create an instant live support room for the current user.
   */
  async createLiveSupportRoom(payload?: {
    source_path?: string;
    source_label?: string;
  }): Promise<LiveSupportRoomResponse> {
    return this.post("/support/live-room", payload || {});
  }

  /**
   * Get live support config (public, no auth needed).
   */
  async getSupportConfig(): Promise<SupportConfigResponse> {
    const response = await fetch(`${this.baseUrl}/support/config`);
    if (!response.ok) return { live_enabled: false, next_available_at: null };
    return response.json();
  }

  /**
   * Update live support config (admin only).
   */
  async updateSupportConfig(payload: {
    live_enabled: boolean;
    next_available_minutes?: number | null;
  }): Promise<SupportConfigResponse> {
    return this.post("/support/config", payload);
  }

  /**
   * Stop notebook execution
   */
  async stopNotebookExecution(
    notebookId: string
  ): Promise<{ status: string; message: string }> {
    const response = await fetch(
      `${this.baseUrl}/notebook/${notebookId}/stop`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to stop notebook: ${response.status} ${errorText}`
      );
    }

    return response.json();
  }

  // ── Trade Engagement ────────────────────────────────────────────────────────

  /**
   * Toggle like on a trade article. Returns { liked, like_count }.
   */
  async toggleTradeLike(
    slug: string,
    userId: string
  ): Promise<{ liked: boolean; like_count: number }> {
    const response = await fetch(`${this.baseUrl}/trades/like`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ slug, user_id: userId }),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to toggle like: ${response.status} ${errorText}`);
    }
    return response.json();
  }

  /**
   * Record a page view (fire-and-forget, deduplicated per user per day).
   */
  async recordTradeView(
    slug: string,
    userId?: string,
    source?: string
  ): Promise<void> {
    try {
      await fetch(`${this.baseUrl}/trades/view`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slug, user_id: userId || null, source: source || null }),
      });
    } catch {
      // fire-and-forget — don't block the page
    }
  }

  /**
   * Record a share-link click (fire-and-forget).
   */
  async recordShareClick(slug: string): Promise<void> {
    try {
      await fetch(
        `${this.baseUrl}/trades/share-click?slug=${encodeURIComponent(slug)}`,
        { method: "POST" }
      );
    } catch {
      // fire-and-forget
    }
  }

  /**
   * Get engagement stats for a single trade article.
   */
  async getTradeStats(
    slug: string,
    userId?: string
  ): Promise<TradeStats> {
    const params = new URLSearchParams({ slug });
    if (userId) params.append("user_id", userId);
    const response = await fetch(
      `${this.baseUrl}/trades/stats?${params.toString()}`
    );
    if (!response.ok) {
      throw new Error("Failed to fetch trade stats");
    }
    return response.json();
  }

  /**
   * Get engagement stats for multiple trade articles (listing page).
   */
  async getTradeBulkStats(
    slugs: string[],
    userId?: string
  ): Promise<TradeBulkStats> {
    const params = new URLSearchParams({ slugs: slugs.join(",") });
    if (userId) params.append("user_id", userId);
    const response = await fetch(
      `${this.baseUrl}/trades/bulk-stats?${params.toString()}`
    );
    if (!response.ok) {
      throw new Error("Failed to fetch bulk trade stats");
    }
    return response.json();
  }
}

// ── Trade Engagement Types ──────────────────────────────────────────────────
export interface TradeStats {
  slug: string;
  like_count: number;
  view_count: number;
  share_click_count: number;
  user_liked: boolean;
}

export interface TradeBulkStats {
  stats: TradeStats[];
}

// Export singleton instance
export const apiClient = new APIClient();

/**
 * SWR-compatible fetcher for session data
 * @param sessionId - The session ID to fetch
 * @param requestingUserId - Optional user ID to check ownership
 */
export const sessionFetcher = async (sessionId: string, requestingUserId?: string) => {
  return apiClient.getSession(sessionId, requestingUserId);
};
